import styled from '@emotion/styled'
import { Cover, device, space, color } from '@ticketswap/solar'
import { useTranslation } from '~/components/TranslationContext'
import SiteSearch from '~/components/SiteSearch'
import Menubar from '~/components/Menubar'
import { MenubarContainer } from '~/components/Menubar'
import { NextSeo } from 'next-seo'
import withBaseProps from '~/server/utils/withBaseProps'
import SiteFooter from '~/components/SiteFooter'
import HomePageContent from '~/components/HomePageContent'
import FeedbackWidget from '~/components/FeedbackWidget'
import Testimonials from '~/components/Testimonials'
import HomepageStructuredData from '~/components/HomepageStructuredData'
import { PageTitle } from '~/components/PageTitle'
import { NarrowPageLayout } from '~/components/PageLayout'
import PartnerBanner from '~/components/SiteFooter/PartnerBanner'
import { GET_COVER } from '~/graphql/queries/getCover'
import { GET_HOMEPAGE_ANNOUNCEMENT } from '~/graphql/queries/getHomepageAnnouncement'
import HomepageAnnouncement from '~/components/HomepageAnnouncement'
import { useState } from 'react'
import serverSideTranslations from '~/server/utils/serverSideTranslations'
import SmartBanner from '~/components/SmartBanner'
import dynamic from 'next/dynamic'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import { useRouter } from 'next/router'
import { useDeviceInfo } from '~/components/DeviceInfoContext'
import { getHostnameByLocale } from '~/utils/localization'

const USPBanner = dynamic(() => import('~/components/USPBanner'), { ssr: false })

const StyledCover = styled(Cover)`
  display: flex;
  min-height: 320px;

  > section {
    align-self: center;
    padding-block-start: ${props => (props.smartBannerVisible ? space[64] : 'unset')};
    padding-block-end: unset;
    width: 100%;
  }

  @media ${device.tablet} {
    height: 50vh;
    justify-content: center;
    align-items: flex-end;
    margin-block-start: unset;
  }
`

const StyledPageTitle = styled(PageTitle)`
  padding-block: 0;
  padding-inline: ${space[16]};
  margin-block: ${space[16]};
  margin-inline: 0;
  text-shadow: 0px 1px 4px rgb(0 0 0 / 30%), 0px 2px 13px rgb(0 0 0 / 30%),
    0px 3px 23px rgb(0 0 0 / 30%);

  h1 {
    color: ${color.lightForeground};
  }

  @media ${device.tablet} {
    text-align: center;
    margin-block: ${space[32]};
    margin-inline: 0;
  }
`

const BackgroundContainer = styled.div``

const Home = ({ hostname, cover, announcement, initialHideSmartBanner }) => {
  const { t } = useTranslation()
  const [menuShown, setMenuShown] = useState(false)
  const [hideSmartBanner, setHideSmartBanner] = useState(initialHideSmartBanner)

  const { loggedInUser } = useLoggedInUser()
  const deviceInfo = useDeviceInfo()
  const { isIOS, isChrome, isMobile } = deviceInfo
  const { locale } = useRouter()
  const showJobOfferings = true
  const isEnglishWebsite = locale === 'en-GB' || locale === 'en'
  const hideBanner =
    Boolean(loggedInUser) ||
    !isIOS ||
    !isChrome ||
    !isMobile ||
    !isEnglishWebsite ||
    hideSmartBanner

  return (
    <>
      <NextSeo
        title={`${t('common:site.name')}: ${t('common:site.title')}`}
        openGraph={{
          title: `${t('common:site.name')}: ${t('common:site.title')}`,
          site_name: t('common:site.name'),
        }}
      />

      <StyledCover
        caption={cover.copyrightText}
        captionUrl={cover.copyrightLink}
        imageUrl={cover.imageSizes.desktop}
        images={cover.imageSizes}
        data-hide-feedback-widget="true"
        withInsetShadow={false}
        smartBannerVisible={!hideBanner}
      >
        <StyledPageTitle primary={t('home:tagline')} />
        <NarrowPageLayout>
          <SiteSearch trigger="homepage" />
          <HomepageAnnouncement announcement={announcement} />
        </NarrowPageLayout>
      </StyledCover>
      <BackgroundContainer>
        <HomePageContent t={t} />
        <Testimonials />
      </BackgroundContainer>
      <FeedbackWidget />
      <PartnerBanner />
      <SiteFooter />
      <HomepageStructuredData hostname={hostname} />

      <MenubarContainer>
        <USPBanner t={t} />
        <SmartBanner
          menuShown={menuShown}
          hideBanner={hideBanner}
          setHideSmartBanner={setHideSmartBanner}
          t={t}
        />

        <Menubar
          hasSearchToggle={false}
          appearance="light"
          showJobOfferings={showJobOfferings}
          showPartnersButton={!showJobOfferings}
          setMenuShown={setMenuShown}
        />
      </MenubarContainer>
    </>
  )
}

export const getServerSideProps = ctx =>
  withBaseProps(ctx, async context => {
    const { locale, req, apolloClient } = context

    const { data } = await apolloClient.query({
      query: GET_COVER,
    })

    const { data: announcementData } = await apolloClient.query({
      query: GET_HOMEPAGE_ANNOUNCEMENT,
    })

    const announcement = announcementData?.announcement?.text || null
    const initialHideSmartBanner = Boolean(req.cookies['hideSmartBanner'])

    return {
      props: {
        hostname: getHostnameByLocale(locale, req),
        cover: data.randomBackgroundImage,
        initialHideSmartBanner,
        announcement,
        ...(await serverSideTranslations(locale, ['common', 'home'])),
      },
    }
  })

export default Home
